/**
 * Enum for all routes in the application
 *
 * @export
 * @enum {string}
 * @description Routing paths for the application
 */
export enum AppRoutes {
  // Home
  Home = '',

  // Login
  Login = 'login',

  // Invitation landing page (#279) — unguarded: the invitee has no account until they redeem.
  Invite = 'invite',

  // Events
  // Events = 'events',
  EventSettings = 'location/event/settings',

  // Authentication
  AuthenticationProfile = 'authentication/profile',
  AuthenticationSigningIn = 'authentication/signing-in',
  // Context chooser (#279): where a session that may act in more than one place picks one. A route rather than
  // an in-app panel because such a session has no settled roles yet, so no shell can frame it correctly.
  AuthenticationChooseContext = 'authentication/choose-context',

  // Configurations
  Configurations = 'configurations',
  //ConfigurationSettings = 'configurations/settings',

  // Locations
  Locations = 'locations',
  LocationSettings = 'locations/settings',

  // Compositions
  Compositions = 'compositions',
  CompositionSettings = 'compositions/settings',
  CompositionTypes = 'compositions/type',
  CompositionTypeSettings = 'compositions/type/settings',

  // Tags
  Tags = 'tags',
  TagSettings = 'tags/settings',

  // Categories
  Categories = 'categories',
  CategorySettings = 'categories/settings',

  // Items
  ItemSettings = "items/settings",
  Items = 'items',

  // Orders
  Orders = 'orders',

  // Analytics
  Analytics = 'analytics',

  // Devices
  DeviceOverview = 'devices/overview',
  DeviceSettings = 'devices/settings',
  // The register-onboarding page: `devices/onboarding/:tab`, tabs `qr` (the rotating setup code a new register
  // scans, #469 step 5) and `credentials` (the reusable client id + password, #469 step 7). One page since #279.
  DeviceOnboarding = 'devices/onboarding',
  // The former separate routes. Kept as redirects into their onboarding tab so old links and bookmarks work.
  DeviceEnrollment = 'devices/enrollment',
  DeviceCredentials = 'devices/credentials',

  // Company
  CompanyModule = 'company',
  CompanySupplier = 'company/supplier',
  CompanySupplierSettings = 'company/supplier/settings',
  CompanySettings = 'company/settings',
  CompanyTelegram = 'company/telegram',
  CompanyEmployee = 'company/employee',
  CompanyEmployeeDownload = 'company/employee/download',
  CompanyEmployeeDetails = 'company/employee/details',
  CompanyEmployeeSettings = 'company/employee/settings',

  // Automation Module
  AutomationModule = 'automation',

  // Inventory Module
  InventoryModule = 'inventory',

  // Location Module
  LocationModule = 'location',

  // Legal Module
  LegalModule = 'legal',
  /**
   * The TSE overview, spelled out as a full path because `AuthGuard` needs somewhere to bounce a confined
   * `Steuerberater` session to and must not reach into a feature module for it (#769).
   *
   * Kept honest by `auth.guard.spec.ts`, which asserts it equals `LegalModule + '/' + LegalRoutes.TSS` — the
   * pair `legal.routes.ts` actually registers.
   */
  LegalTse = 'legal/tss',

  // owner module
  OwnerModule = 'owner',

  // selfservice module
  SelfServiceModule = 'selfservice',

  // Admin
  AdminUsers = 'admin/users',
  AdminPatches = 'admin/patches',


  // Owner
  OwnerCompanies = 'owner/companies',
  OwnerCompanySettings = 'owner/companies/settings',
  OwnerUsers = 'owner/users',
  OwnerEvents = 'owner/events',
  OwnerShards = 'owner/shards',
  OwnerShardSettings = 'owner/shards/settings',
  // REMOVED (#608): `OwnerEnvironmentSettings = 'owner/environment'`, the raw key→value editor over
  // `EnvironmentSetting`. Every platform-wide key it exposed now has a typed field — the integration
  // credentials on `owner/integrations`, the rest on `owner/platform` (`OwnerRoutes.Platform`) — so the editor
  // was the only screen on which a value could be set to something no reader understands. The
  // `Management/Environment/*` API stays: the admin patches screen, the migrations assistant and the operator
  // console all still use it. `/owner/environment` now falls through the owner module's catch-all to '/'.
  OwnerGeolocationTracking = 'owner/geolocation-tracking',

  Changelog = 'changelog',

  // Audit trail viewer (Auditor / PlatformOwner / CompanyOwner)
  AuditLog = 'audit',

  // EmployeePanel
  EmployeePanel = 'ep',
  EmployeePanelLogin = 'ep/login',
  // Forms-only completion screen for a required-forms gated (restricted) employee session.
  FormsRequired = 'forms-required',

  // Two-factor remediation screen for an account-security restricted session (#282). Deliberately NOT under
  // `ep/` and not inside any panel: `accountSecurity` mirrors a company-wide policy that restricts management
  // users and platform owners exactly as it restricts employees, so the one screen such a session may reach has
  // to sit outside every shell — the same shape as FormsRequired above, but for every kind of session.
  TwoFactorRequired = 'two-factor-required',

  // Contact-linking screen for a contact-gated (restricted) employee session (#558). Sits beside
  // FormsRequired rather than under `ep/` for the same reason: a restricted session must have nowhere to
  // navigate but the one screen that lifts its restriction, and a panel shell offers a sidebar full of routes
  // the guard is going to bounce straight back here. Employee-only, like the forms gate — so unlike
  // TwoFactorRequired above it carries the employee role set on its route data.
  ContactRequired = 'contact-required',

  // Waiting screen for an employee whose employment has not started yet (#864). Outside every shell for the same
  // reason as the three screens above: the session may reach only the forms, contact and two-factor screens, and
  // a panel sidebar would be a list of links the guard bounces back here. Employee-only.
  EmploymentPending = 'employment-pending',

  // Operator panel (#205) — the standalone baseline role's own minimal shell.
  OperatorPanel = 'operator',

  // Feedback
  Feedback = 'feedback',

  MailingModule = 'mailing',
}



